// SARA chat client — streams SSE from the FastAPI backend.
//   • Web (browser / Vite dev / Caddy prod): same-origin, relative /v1 (empty base).
//   • Native (Capacitor iOS/Android): app is served from a local origin, so it calls the
//     API cross-origin at the absolute URL below (backend CORS is open).
const isNative =
  typeof window !== 'undefined' &&
  // @ts-expect-error — Capacitor global is injected only inside the native WebView
  !!(window.Capacitor?.isNativePlatform?.())

export const API_BASE = isNative ? 'https://coach.innovaccer.app' : ''

// Bearer token (set by the auth layer) so the backend scopes data to the signed-in user.
function tokenHeader(): Record<string, string> {
  try {
    const t = localStorage.getItem('sara-token')
    return t ? { Authorization: `Bearer ${t}` } : {}
  } catch {
    return {}
  }
}

export type ChatMsg = { role: 'user' | 'assistant'; content: string }

export type PersonaMeta = {
  persona: string
  persona_label: string
  accent: string
  tenant: string
}

export type StreamHandlers = {
  onMeta?: (m: PersonaMeta) => void
  onDelta?: (text: string) => void
  onDone?: (info: { persona: string; guardrail?: boolean }) => void
  onError?: (err: string) => void
}

export async function streamChat(
  userId: string,
  messages: ChatMsg[],
  handlers: StreamHandlers,
  opts?: { companion?: string; signal?: AbortSignal; inCall?: boolean },
): Promise<void> {
  let resp: Response
  try {
    resp = await fetch(`${API_BASE}/v1/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...tokenHeader() },
      body: JSON.stringify({ user_id: userId, messages, companion: opts?.companion, in_call: !!opts?.inCall }),
      signal: opts?.signal,
    })
  } catch {
    handlers.onError?.('Network error — is the SARA API reachable?')
    return
  }
  if (!resp.ok || !resp.body) {
    handlers.onError?.(`HTTP ${resp.status}`)
    return
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let sep: number
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const raw = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      let event = 'message'
      let data = ''
      for (const line of raw.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) data += line.slice(5).trim()
      }
      if (!data) continue
      let parsed: unknown
      try {
        parsed = JSON.parse(data)
      } catch {
        parsed = data
      }
      if (event === 'meta') handlers.onMeta?.(parsed as PersonaMeta)
      else if (event === 'delta') handlers.onDelta?.(String(parsed))
      else if (event === 'done') handlers.onDone?.(parsed as { persona: string; guardrail?: boolean })
    }
  }
  handlers.onDone?.({ persona: '' })
}

export async function fetchNudge(userId: string, companion?: string): Promise<{ text: string; speaker: string } | null> {
  try {
    const r = await fetch(`${API_BASE}/v1/nudge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...tokenHeader() },
      body: JSON.stringify({ user_id: userId, companion }),
    })
    if (!r.ok) return null
    return await r.json()
  } catch {
    return null
  }
}

export type Persona = { key: string; label: string; accent: string; tools: string[] }

export async function fetchPersonas(): Promise<Persona[]> {
  try {
    const r = await fetch(`${API_BASE}/v1/personas`)
    const j = await r.json()
    return j.personas ?? []
  } catch {
    return []
  }
}
